/**
 * MM Cart V3 - Version ULTIME STABILITÉ (Queue, Auto-Retry & Isolation)
 */
const MM_AUTH_HEADER = { 'X-MM-Auth': 'genuine' };
const MM_ROOT_URL = window.Shopify?.routes?.root || '/';
const mmUrl = (path) => MM_ROOT_URL + path.replace(/^\//, '');
const isLocal = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';

// GESTIONNAIRE DE FILE D'ATTENTE (FIFO) AVANT-GARDE
class RequestQueue {
    constructor() {
        this.queue = [];
        this.processing = false;
        this.lastCompletion = Date.now();
        this.isPageLoading = true;
        
        // On attend que la page soit stable (1s sur Windows local pour éviter les race conditions au reload)
        const loadDelay = isLocal ? 1000 : 100;
        setTimeout(() => { this.isPageLoading = false; this.process(); }, loadDelay);
    }

    async add(task) {
        return new Promise((resolve, reject) => {
            this.queue.push({ task, resolve, reject });
            if (!this.isPageLoading) this.process();
        });
    }

    async process() {
        if (this.processing || this.queue.length === 0 || this.isPageLoading) return;
        this.processing = true;

        const { task, resolve, reject } = this.queue.shift();

        // Délai de précaution optimisé (500ms sur local suffisent souvent, 50ms en prod pour la réactivité)
        const gap = isLocal ? 500 : 50; 
        const now = Date.now();
        if (now - this.lastCompletion < gap) {
            const wait = gap - (now - this.lastCompletion);
            await new Promise(r => setTimeout(r, wait));
        }

        const execute = async (retryCount = 0) => {
            try {
                const res = await task();
                // Si 429 (Too Many Requests), on attend de plus en plus longtemps
                if (res && res.status === 429) {
                    if (retryCount < 5) {
                        const delay = Math.min(1000 * Math.pow(2, retryCount), 30000); // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
                        console.warn(`[MM-Queue] 429 détecté. Tentative ${retryCount + 1}/5 dans ${delay/1000}s...`);
                        await new Promise(r => setTimeout(r, delay));
                        return await execute(retryCount + 1);
                    } else {
                        console.error("[MM-Queue] Trop d'echecs 429. Abandon.");
                    }
                }
                this.lastCompletion = Date.now();
                resolve(res);
            } catch (e) {
                console.error("[MM-Queue] Erreur critique:", e);
                reject(e);
            }
        };

        await execute();
        this.processing = false;
        // On laisse une petite respiration avant le prochain si la file est longue
        setTimeout(() => this.process(), 20); // Réduit à 20ms
    }
}

const CartQueue = new RequestQueue();

// --- ACCESSIBILITY / FOCUS TRAP ---
let focusTrap = {
    active: false,
    el: null,
    lastFocused: null,
    keydownHandler: null
};

function getVisibleFocusables(element) {
    return Array.from(element.querySelectorAll('a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'))
        .filter(el => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden');
}

function activateFocusTrap(element) {
    if (!element) return;
    deactivateFocusTrap(); // Clean up any existing trap first

    focusTrap.active = true;
    focusTrap.el = element;
    focusTrap.lastFocused = document.activeElement;

    // Focus first element (active wait for viability)
    const focusFirst = () => {
        if (!focusTrap.active || focusTrap.el !== element || element.contains(document.activeElement)) return;
        const firstFocusable = getVisibleFocusables(element)[0];
        if (firstFocusable) firstFocusable.focus();
    };
    requestAnimationFrame(focusFirst);
    setTimeout(focusFirst, 150);

    focusTrap.keydownHandler = function(e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
            if (document.getElementById('mmCartImageModal')?.classList.contains('mm-open')) {
                mmCloseImageModal();
                return;
            }
            mmCloseCart();
            return;
        }
        if (e.key !== 'Tab' && e.keyCode !== 9) return;
        const focusables = getVisibleFocusables(element);
        if (!focusables.length) return;
        const firstFocusable = focusables[0];
        const lastFocusable = focusables[focusables.length - 1];
        const focusInside = element.contains(document.activeElement);
        if (e.shiftKey) {
            if (!focusInside || document.activeElement === firstFocusable) { e.preventDefault(); lastFocusable.focus(); }
        } else if (!focusInside || document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
        }
    };

    document.addEventListener('keydown', focusTrap.keydownHandler);
}

function deactivateFocusTrap() {
    if (!focusTrap.active) return;
    focusTrap.active = false;
    if (focusTrap.lastFocused) focusTrap.lastFocused.focus();

    // Remove the stored event listener
    if (focusTrap.keydownHandler) {
        document.removeEventListener('keydown', focusTrap.keydownHandler);
    }

    focusTrap.el = null;
    focusTrap.lastFocused = null;
    focusTrap.keydownHandler = null;
}

// --- HELPERS ---
function getCartDrawerWrapper() { return document.querySelector('.mm-drawer-cart-wrapper'); }
function getCartDrawer() { return document.querySelector('.mm-drawer-cart'); }

function getHeaderSectionId() {
    const container = document.querySelector('.mm-header-cart-container');
    if (container?.dataset.sectionId) return container.dataset.sectionId;
    const section = container?.closest('.shopify-section');
    return section?.id?.replace('shopify-section-', '') || 'MM-header';
}

function getCartSectionId() { 
    const drawerId = getCartDrawerWrapper()?.dataset?.sectionId || 'mm-cart';
    const headerId = getHeaderSectionId();
    
    // Auto-detect rewards section
    const rewardsContainer = document.querySelector('.mm-reward-data');
    let rewardsSectionId = null;
    if (rewardsContainer) {
        const sect = rewardsContainer.closest('.shopify-section');
        if (sect) rewardsSectionId = sect.id.replace('shopify-section-', '');
    }

    const ids = new Set([drawerId, headerId]);
    if (rewardsSectionId) ids.add(rewardsSectionId);
    
    return Array.from(ids).join(',');
}

function openCart() {
	const wrapper = getCartDrawerWrapper();
	if (wrapper) {
		wrapper.classList.add('mm-dc-is-open');
		document.body.classList.add('mm-cart-open');
        document.documentElement.style.overflow = 'hidden';
        activateFocusTrap(wrapper);
	}
}

let splideInstance = null;
let splideRewardsInstances = [];

function mmInitSplideRewards() {
    splideRewardsInstances.forEach(inst => {
        try { inst.destroy(); } catch(e) {}
    });
    splideRewardsInstances = [];

    document.querySelectorAll('.mm-splide-rewards').forEach(elem => {
        if (typeof Splide !== 'undefined') {
            const inst = new Splide(elem, {
                type: 'slide',
                arrows: true,
                pagination: false,
                rewind: false,
                gap: 8,
                perPage: 1,
            }).mount();
            splideRewardsInstances.push(inst);
        }
    });
}

function mmInitSplideCross() {
	const splideElem = document.querySelector(".mm-splide-crossell");
	if (splideElem && typeof Splide !== 'undefined') {
        // Destroy old instance if it exists
        if (splideInstance) {
            try { splideInstance.destroy(); } catch(e) { console.warn("[MM] Splide destroy error:", e); }
            splideInstance = null;
        }

		splideInstance = new Splide(splideElem, {
			type: "slide",
			arrows: true,
			pagination: false,
			rewind: false,
			gap: 12,
			perPage: 1,
			padding: { left: '40px', right: '40px' }
		}).mount();
	}
}

// --- CORE API ---
// --- HELPERS FOR UI ---
// --- HELPERS FOR UI ---
function showLoaderOnPrice(container) {
    if (!container) return;
    const priceEl = container.querySelector('.mm-dc-product-price-wrapper')
                 || container.querySelector('.mm-dc-product-price-container')
                 || container.querySelector('.mm-product-price');

    // Fallback: apply to container if specific price element not found
    const target = priceEl || container;

    if (window.mmLoadThis) window.mmLoadThis(target);
    else target.classList.add('mm-loading');
}

async function mmResolveLineKey(staleKey) {
    const cart = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER })
        .then(r => r.ok ? r.json() : null)
        .catch(() => null);
    const items = cart?.items || [];
    if (items.some(i => i.key === staleKey)) return staleKey;
    const variantId = staleKey.split(':')[0];
    const sameVariant = items.filter(i => String(i.variant_id) === variantId);
    const isGift = i => i.properties?._is_reward === 'true' || !!i.properties?._reward_id;
    return (sameVariant.find(i => !isGift(i)) || sameVariant[0])?.key || null;
}

// --- CORE API ---
async function changeQuantity(id, quantity, isLine = false, productId = null, sellingPlan = undefined, properties = null) {
	const body = isLine ? { line: id.toString(), quantity, sections: getCartSectionId() } : { id: id.toString(), quantity, sections: getCartSectionId() };
    if (sellingPlan !== undefined) body.selling_plan = (sellingPlan === '0' || sellingPlan === 0) ? null : sellingPlan;
    if (properties !== null) body.properties = properties;

    if (isLine) {
        // VISUAL FEEDBACK: Show loader on price if finding element by line
        const itemEl = document.querySelector(`[data-line-index="${id}"]`);
        showLoaderOnPrice(itemEl);
    }

    // INTERCEPTION (OPTIMIZED): Check via DOM/VariantId instead of fetching full cart
    if (parseInt(quantity) === 0) {
        try {
            let variantId = null;
            if (isLine && typeof id !== 'object') {
                 const itemEl = document.querySelector(`[data-line-index="${id}"]`);
                 if (itemEl) {
                     // Try to find variant ID from various common attributes
                     variantId = parseInt(itemEl.dataset.cartItemId || itemEl.dataset.variantId || itemEl.getAttribute('data-variant-id') || itemEl.getAttribute('data-cart-item-id'));

                     // If not found on container, try to find a link with variant param inside
                     if (!variantId) {
                         const link = itemEl.querySelector('a[href*="variant="]');
                         if (link) {
                             const match = link.href.match(/variant=(\d+)/);
                             if (match) variantId = parseInt(match[1]);
                         }
                     }
                 }
            } else if (!isLine) {
                 variantId = parseInt(id);
            }

            if (variantId) {
                 // Check against known rewards in the page
                 const allRewards = document.querySelectorAll('.mm-reward-data');
                 allRewards.forEach(r => {
                     // If this product is part of any reward definition (auto-add or not), we mark it declined
                     const prods = JSON.parse(r.getAttribute('data-products') || '[]');

                     if (prods.some(p => parseInt(p.variantId) === variantId)) {
                         const rId = r.getAttribute('data-reward-id');
                         sessionStorage.setItem('mm_declined_reward_' + rId, 'true');

                         // Si c'est un cadeau lié à un code promo, on supprime aussi le code
                         if (r.getAttribute('data-reward-type') === 'free_product_code') {
                            const code = (r.getAttribute('data-required-code') || '').toUpperCase().trim();
                            if (code) {
                                // 1. Nettoyage SessionStorage
                                let freeCodes = JSON.parse(sessionStorage.getItem('mm_applied_free_codes') || '[]');
                                const newFreeCodes = freeCodes.filter(c => c.toUpperCase() !== code);
                                if (newFreeCodes.length !== freeCodes.length) {
                                    sessionStorage.setItem('mm_applied_free_codes', JSON.stringify(newFreeCodes));
                                }
                                
                                // 2. Suppression Shopify (via une tâche en file d'attente qui passera après)
                                CartQueue.add(async () => {
                                    return fetch(mmUrl('/cart/update.js'), {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                                        body: JSON.stringify({ discount: '', sections: getCartSectionId() })
                                    });
                                });
                            }
                         }
                     }
                 });
            }
        } catch(e) { console.error("Error checking reward decline (DOM):", e); }
    }

	return CartQueue.add(async () => {
		try {
			const postChange = () => fetch(mmUrl('/cart/change.js'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
				body: JSON.stringify(body)
			});
			let response = await postChange();
			if (!response.ok && !isLine && String(id).includes(':') && [400, 404, 422].includes(response.status)) {
				const freshKey = await mmResolveLineKey(String(id));
				if (freshKey && freshKey !== body.id) {
					body.id = freshKey;
					response = await postChange();
				}
			}
			if (response.ok) {
				const data = await response.json();
				await updateUI(data, productId);
			} else {
				console.error('[MM-Cart] /cart/change.js a echoue:', response.status, await response.clone().text());
				await mmRefreshCartAndRecomputeGifts();
				mmSetCartBusy(false);
			}
            return response;
		} catch (e) {
			console.error('[MM-Cart] Erreur reseau sur /cart/change.js:', e);
			await mmRefreshCartAndRecomputeGifts();
			mmSetCartBusy(false);
			return null;
		}
	});
}

let cartSnapshot = [];

async function syncCartState() {
    try {
        const response = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER });
        const data = await response.json();
        if (!data || !data.items) return;
        
        const newIds = Array.from(new Set(data.items.map(it => it.product_id)));
        const allIds = Array.from(new Set([...cartSnapshot, ...newIds]));
        
        const toRefresh = [];
        allIds.forEach(id => {
            const wasIn = cartSnapshot.includes(id);
            const isIn = newIds.includes(id);
            if (wasIn !== isIn) toRefresh.push(id);
        });

        cartSnapshot = newIds;
        toRefresh.forEach(id => {
            if (window.refreshProductCard) window.refreshProductCard(id);
        });

        // Trigger an extra refresh for any ID explicitly mentioned in products that joined
        // (Handled by toRefresh, but useful for quantity changes if we want to be aggressive)
    } catch (e) {
        console.error("[MM-Queue] Echec syncCartState:", e);
    }
}

// --- REWARD LOGIC REFACTORED ---

const mmRewardAddInFlight = new Set();

function analyzeRewards(cartData, rootElement = document) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    const rewards = rootElement.querySelectorAll('.mm-reward-data');
    const updates = {};
    const itemsToAdd = [];
    let needsUpdates = false;

    // Helper secure parse
    const safeParse = (str) => {
        try { return JSON.parse(str || '[]'); } catch { return []; }
    };

    // Étape 1 : Collecter tous les produits qui sont protégés (élgibles)
    const protectedVariants = new Set();
    const hasSubscriptionInJSON = items.some(it => {
        const hasPlan = !!it.selling_plan || !!it.selling_plan_allocation;
        const hasPropP = it.properties && (it.properties.selling_plan && it.properties.selling_plan !== 'null' && it.properties.selling_plan !== '0');
        const hasPropU = it.properties && (it.properties._selling_plan && it.properties._selling_plan !== 'null' && it.properties._selling_plan !== '0');
        return hasPlan || hasPropP || hasPropU;
    });
    let freeCodesFromSession = safeParse(sessionStorage.getItem('mm_applied_free_codes')).map(c => c.toUpperCase().trim());
    if (Array.isArray(cartData?.discount_codes)) {
        const codesOnCart = new Set(cartData.discount_codes.map(d => (d.code || '').toUpperCase().trim()));
        const keptCodes = freeCodesFromSession.filter(c => codesOnCart.has(c));
        if (keptCodes.length !== freeCodesFromSession.length) {
            freeCodesFromSession = keptCodes;
            sessionStorage.setItem('mm_applied_free_codes', JSON.stringify(keptCodes));
        }
    }

    rewards.forEach(reward => {
        const type = reward.getAttribute('data-reward-type');
        const offerActive = reward.getAttribute('data-offer-active') === 'true';
        let isEligible = reward.getAttribute('data-is-eligible') === 'true';
        
        // Pour free_product_code : Shopify BXGY ne peuple jamais discount_applications avant que
        // le produit gratuit soit en panier. On valide via sessionStorage + threshold du bloc.
        if (type === 'free_product_code') {
            const requiredCode = (reward.getAttribute('data-required-code') || '').toUpperCase().trim();
            if (requiredCode) {
                const thresh = parseInt(reward.getAttribute('data-threshold') || '0');
                const cartTotalNow = Array.isArray(cartData?.items) ? (cartData.total_price || 0) : 0;
                const spendMet = thresh === 0 || cartTotalNow >= thresh;
                isEligible = offerActive && spendMet && freeCodesFromSession.includes(requiredCode);
            }
        }

        if (type === 'free_product_abon') {
            isEligible = offerActive && hasSubscriptionInJSON; 
        }

        if (type === 'free_product_simple') {
            const threshold = parseInt(reward.getAttribute('data-threshold') || '0');
            const cartTotal = cartData?.total_price || 0;
            isEligible = offerActive && (cartTotal >= threshold);
        }

        if (isEligible) {
            const products = safeParse(reward.getAttribute('data-products'));
            products.forEach(p => protectedVariants.add(parseInt(p.variantId)));
        }
    });

    // Étape 2 : Analyser chaque offre
    rewards.forEach(reward => {
        const rewardId = reward.getAttribute('data-reward-id');
        const type = reward.getAttribute('data-reward-type');
        const offerActive = reward.getAttribute('data-offer-active') === 'true';
        let isEligible = reward.getAttribute('data-is-eligible') === 'true';

        // --- TRACKING TRANSITION ---
        const eligibilityKey = 'mm_last_eligible_' + rewardId;
        const wasEligible = sessionStorage.getItem(eligibilityKey) === 'true';
        
        if (isEligible && !wasEligible) {
            sessionStorage.removeItem('mm_declined_reward_' + rewardId);
        }
        sessionStorage.setItem(eligibilityKey, isEligible);

        if (type === 'free_product_code') {
            const requiredCode = (reward.getAttribute('data-required-code') || '').toUpperCase().trim();
            if (requiredCode) {
                const prevElig = isEligible;
                const thresh = parseInt(reward.getAttribute('data-threshold') || '0');
                const cartTotalNow = cartData?.total_price || 0;
                const spendMet = thresh === 0 || cartTotalNow >= thresh;
                const codeInSession = freeCodesFromSession.includes(requiredCode);

                // Montant redescendu sous le seuil → retirer le code de la session
                if (!spendMet && codeInSession) {
                    const idx = freeCodesFromSession.indexOf(requiredCode);
                    if (idx !== -1) freeCodesFromSession.splice(idx, 1);
                    sessionStorage.setItem('mm_applied_free_codes', JSON.stringify(freeCodesFromSession));
                }

                isEligible = offerActive && spendMet && freeCodesFromSession.includes(requiredCode);
                if (isEligible && !prevElig && !wasEligible) sessionStorage.removeItem('mm_declined_reward_' + rewardId);
                sessionStorage.setItem(eligibilityKey, isEligible);
            }
        }
        
        if (type === 'free_product_abon') {
             const prevElig = isEligible;
             isEligible = offerActive && hasSubscriptionInJSON; // Must respect dates
             if (isEligible && !prevElig && !wasEligible) sessionStorage.removeItem('mm_declined_reward_' + rewardId);
        }

        if (type === 'free_product_simple') {
            const prevElig = isEligible;
            const threshold = parseInt(reward.getAttribute('data-threshold') || '0');
            const cartTotal = cartData?.total_price || 0;
            isEligible = offerActive && (cartTotal >= threshold);
            if (isEligible && !prevElig && !wasEligible) sessionStorage.removeItem('mm_declined_reward_' + rewardId);
            sessionStorage.setItem(eligibilityKey, isEligible);
        }

        const products = safeParse(reward.getAttribute('data-products'));
        const autoAddEnabled = reward.getAttribute('data-auto-add-enabled') === 'true';
        const declined = sessionStorage.getItem('mm_declined_reward_' + rewardId) === 'true';

        // A. Suppression
        if (!isEligible) {
            products.forEach(p => {
                const variantId = parseInt(p.variantId);
                const item = items.find(it => parseInt(it.variant_id) === variantId);
                
                if (item && !protectedVariants.has(variantId)) {
                    const isReward = item.properties?._is_reward === 'true' || item.properties?.is_reward === 'true' || item.original_price === 0;
                    // STRICT SAFETY: Only remove items marked as rewards, or offered products sold at 0.
                    if (!isReward) return;

                    // Grace period check (optional but safe)
                    const now = Date.now();
                    const addedAt = item.properties?._added_at ? parseInt(item.properties._added_at) : 0;
                    if (addedAt && (now - addedAt < 2000)) return;

                    updates[item.key || item.id] = 0;
                    needsUpdates = true;
                }
            });
        }
        
        // B. Ajout automatique
        const anyProductInCart = products.some(p => items.some(it => parseInt(it.variant_id) === parseInt(p.variantId)));
        
        if (isEligible && declined && anyProductInCart) {
             products.forEach(p => {
                const variantId = parseInt(p.variantId);
                const item = items.find(it => parseInt(it.variant_id) === variantId);
                if (item) {
                     const isReward = item.properties?._is_reward === 'true' || item.properties?.is_reward === 'true';
                     if (isReward && !protectedVariants.has(variantId)) { 
                         updates[item.key || item.id] = 0;
                         needsUpdates = true;
                     }
                }
             });
        }

        const shouldAutoAdd = isEligible && autoAddEnabled && products.length === 1 && !anyProductInCart && !declined;

        if (shouldAutoAdd) {
            const variantId = products[0]?.variantId;
            if (variantId && !mmRewardAddInFlight.has(String(variantId))) {
                itemsToAdd.push({ 
                    id: variantId.toString(), 
                    quantity: 1, 
                    properties: { 
                        _is_reward: 'true',
                        _reward_id: rewardId,
                        _reward_type: type,
                        _added_at: Date.now().toString() 
                    } 
                });
            }
        }
    });

    return { itemsToAdd, updates, needsUpdates };
}

async function executeRewardActions(actions, immediate = false) {
    const logic = async () => {
         const { updates } = actions;
         let itemsToAdd = actions.itemsToAdd;
         let currentData = null;
         let resAdd = null, resUp = null;

         if (itemsToAdd.length > 0) {
            itemsToAdd = itemsToAdd.filter(i => !mmRewardAddInFlight.has(String(i.id)));
            const reservedIds = itemsToAdd.map(i => String(i.id));
            reservedIds.forEach(id => mmRewardAddInFlight.add(id));
            setTimeout(() => reservedIds.forEach(id => mmRewardAddInFlight.delete(id)), 2000);
            try {
                const fresh = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER }).then(r => r.ok ? r.json() : null);
                if (fresh && Array.isArray(fresh.items)) {
                    const present = new Set(fresh.items.map(i => String(i.variant_id)));
                    itemsToAdd = itemsToAdd.filter(i => !present.has(String(i.id)));
                }
            } catch (e) {}
         }

         if (itemsToAdd.length > 0) {
            resAdd = await fetch(mmUrl('/cart/add.js'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                body: JSON.stringify({ items: itemsToAdd, sections: getCartSectionId() })
            });
            if (resAdd.ok) currentData = await resAdd.json();
         }
         
         if (Object.keys(updates).length > 0) {
            resUp = await fetch(mmUrl('/cart/update.js'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                body: JSON.stringify({ updates, sections: getCartSectionId() })
            });
            if (resUp.ok) currentData = await resUp.json();
         }
         return currentData;
    };

    if (immediate) return logic();
    return CartQueue.add(logic);
}

let isSynchronizingRewards = false;
async function mmSynchronizeRewards(data, immediate = false) {
    if (isSynchronizingRewards || !data) return;
    
    let cartData = data;
    if (!cartData.items) {
        try {
            const res = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER });
            if (res.ok) cartData = await res.json();
            else return;
        } catch(e) { return; }
    }

    const actions = analyzeRewards(cartData, document);
    
    if (actions.needsUpdates || actions.itemsToAdd.length > 0) {
        isSynchronizingRewards = true;
        try {
            const newData = await executeRewardActions(actions, immediate);
            if (newData) await updateUI(newData, null, true);
        } finally {
            isSynchronizingRewards = false;
        }
    }
}

function applySection(html) {
    if (!html) return false;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const content = temp.querySelector('.mm-drawer-cart');
    const drawer = getCartDrawer();
    if (content && drawer) {
        drawer.querySelectorAll('[data-accordion-toggle].mm-open').forEach(openToggle => {
            const key = openToggle.getAttribute('data-accordion-toggle');
            const nextToggle = content.querySelector(`[data-accordion-toggle="${key}"]`);
            const nextPanel = content.querySelector(`[data-accordion-content="${key}"]`);
            if (!nextToggle || !nextPanel) return;
            nextToggle.setAttribute('data-open-on-load', '');
            nextPanel.classList.add('mm-no-animation');
        });
        const pendingNote = drawer.querySelector('#CartNote')?.value;
        const activeEl = document.activeElement;
        let focusIndex = -1;
        let focusKey = null;
        if (activeEl && drawer.contains(activeEl)) {
            focusIndex = getVisibleFocusables(drawer).indexOf(activeEl);
            focusKey = activeEl.id || activeEl.getAttribute('onclick') || activeEl.getAttribute('aria-label');
        }
        drawer.innerHTML = content.innerHTML;
        const nextNote = drawer.querySelector('#CartNote');
        if (nextNote && pendingNote !== undefined) nextNote.value = pendingNote;
        if (focusIndex > -1) {
            const candidates = getVisibleFocusables(drawer);
            const sameControl = focusKey && candidates.find(el => (el.id || el.getAttribute('onclick') || el.getAttribute('aria-label')) === focusKey);
            const nextFocus = sameControl || candidates[Math.min(focusIndex, candidates.length - 1)];
            if (nextFocus) nextFocus.focus({ preventScroll: true });
        }
        if (content.dataset.cartCount) drawer.dataset.cartCount = content.dataset.cartCount;
        return true;
    }
    return false;
}

async function updateUI(data, productId = null, skipSync = false) {
	if (!data) return;
	const sections = data.sections || {};
	let found = false;

    const cartIdTotal = getCartSectionId(); // "drawerId,headerId"
    const cartId = cartIdTotal.split(',')[0];
    const headerId = cartIdTotal.split(',')[1];

    // 1. Prepare HTML and Scavenge Data (Optimization)
    // Avoid fetching /cart.js on 'add' actions by reading the returned drawer HTML immediately
    let tempDiv = null;
    let fullCart = data;
    
    if (sections[cartId]) {
        tempDiv = document.createElement('div');
        tempDiv.innerHTML = sections[cartId];
    }

    if (!data.items || data.item_count === undefined) {
        if (tempDiv) {
            // OPTIMIZATION: Parse items from DOM instead of fetching API
            const scavengedItems = Array.from(tempDiv.querySelectorAll('[data-cart-item-id]')).map(el => ({
                variant_id: parseInt(el.dataset.cartItemId),
                key: el.dataset.key || el.dataset.cartItemId,
                quantity: parseInt(el.querySelector('[data-item-quantity]')?.dataset.itemQuantity || 0),
                properties: { _scavenged: true } // Mark as partial data
            }));
            
            // Calculate approximate count
            const scavengedCount = scavengedItems.reduce((acc, it) => acc + it.quantity, 0);
            
            // If data came from 'add.js', it often lacks a proper items array but has sections
            fullCart = { ...data, items: scavengedItems, item_count: scavengedCount, _is_scavenged: true };
          //   console.log("[MM-Performance] Scavenged items from HTML section.");
        } else {
            // Fallback: Must fetch if no HTML available
            try {
                const res = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER });
                if (res.ok) fullCart = await res.json();
                if (data.sections) fullCart.sections = data.sections;
            } catch (e) { console.error("[MM] Error fetching full cart:", e); }
        }
    }

    const orphanParentKeys = mmOrphanRoutineParentKeys(fullCart);
    if (orphanParentKeys.length > 0) {
        try {
            const res = await mmPostJSON(mmUrl('/cart/update.js'), { updates: Object.fromEntries(orphanParentKeys.map(key => [key, 0])), sections: cartIdTotal });
            if (res.ok) {
                await updateUI(await res.json(), productId, skipSync);
                return;
            }
        } catch (e) { console.error('[MM-Routine] Echec du retrait de la routine vide:', e); }
    }

    // 2. Intercept Gift Logic (No Flicker)
    if (!skipSync && tempDiv) {
        try {
            // Si les données ont été récupérées par "scavenging", elles manquent de détails (propriétés d'abonnement).
            // On FORCE un fetch /cart.js pour avoir un état 100% fiable avant d'analyser les cadeaux.
            if (fullCart._is_scavenged || !fullCart.items) {
                 const res = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER });
                 if (res.ok) {
                     const freshCart = await res.json();
                     fullCart = { ...freshCart, sections: fullCart.sections };
                 }
            }

            const actions = analyzeRewards(fullCart, tempDiv);
            
            if (actions.needsUpdates || actions.itemsToAdd.length > 0) {
                const newData = await executeRewardActions(actions, true);
                if (newData) {
                    await updateUI(newData, productId, true); // Recurse with final data
                    return; // ABORT this render (user sees final state directly)
                }
            }
        } catch (e) { console.error("[MM-Rewards] Interception error", e); }
    }

    const finalize = async (newSections = null) => {
        mmPostUpdateUI();
        mmDisplayFreeProductCodes();
        syncCartState();
        if (!skipSync) mmSynchronizeRewards(fullCart);
        updateCartCount(fullCart);
        if (productId && window.refreshProductCard) window.refreshProductCard(productId);

        let headerUpdated = false;
        if (newSections) {
            headerUpdated = refreshHeaderFromSections(newSections);
        }
        
        const count = fullCart.item_count;
        if ((!headerUpdated || count <= 1) && headerId) {
            (async () => {
                try {
                    const res = await fetch(mmUrl(`/?sections=${headerId}`), { headers: MM_AUTH_HEADER });
                    if (res.ok) {
                        const newSections = await res.json();
                        refreshHeaderFromSections(newSections);
                    }
                } catch (err) {
                    console.error("[MM-Queue] Echec fetch header in finalize:", err);
                }
            })();
        }
    };

    // Try to update drawer from current data sections
    if (sections[cartId]) {
        found = applySection(sections[cartId]);
    } else {
        for (const key in sections) {
            if (sections[key]?.includes('mm-drawer-cart')) {
                found = applySection(sections[key]);
                if (found) break;
            }
        }
    }
    
    if (!found) {
        fetch(mmUrl(`/?sections=${cartIdTotal}`), { headers: MM_AUTH_HEADER })
            .then(res => res.json())
            .then(newSections => {
                const drawerHTML = newSections[cartId] || Object.values(newSections).find(s => s.includes('mm-drawer-cart'));
                applySection(drawerHTML);
                finalize(newSections);
            })
            .catch(err => {
                console.error("[MM-Queue] Echec refresh UI:", err);
                finalize();
            });
    } else {
        finalize(sections);
    }
}

function updateCartCount(data) {
    if (!data) return;
    const count = data.item_count !== undefined ? mmVisibleItemCount(data) :
                 (data.cart ? mmVisibleItemCount(data.cart) :
                 (getCartDrawer()?.dataset.cartCount || document.querySelector('[data-cart-count]')?.dataset.cartCount));
                 
	if (count !== undefined) {
		document.querySelectorAll('.mm-cart-count-bubble').forEach(b => {
			b.textContent = count;
			b.style.display = parseInt(count) > 0 ? 'flex' : 'none';
		});
	}
}

function refreshHeaderFromSections(sections) {
    const headerCart = document.querySelector('.mm-header-cart-container');
    if (!headerCart || !sections) return false;
    let found = false;
    for (const key in sections) {
        if (sections[key]?.includes('mm-header-cart-container')) {
            const temp = document.createElement('div');
            temp.innerHTML = sections[key];
            const newHeaderCart = temp.querySelector('.mm-header-cart-container');
            if (newHeaderCart) {
                headerCart.innerHTML = newHeaderCart.innerHTML;
                found = true;
            }
            break;
        }
    }
    return found;
}

// Consolidated with version at the end of file

// --- LOGIC FUNCTIONS ---

let mmCartBusyTimeout = null;

function mmSetCartBusy(isBusy, originCard = null) {
    const wrapper = getCartDrawerWrapper();
    if (!wrapper) return;

    if (mmCartBusyTimeout) {
        clearTimeout(mmCartBusyTimeout);
        mmCartBusyTimeout = null;
    }

    wrapper.querySelectorAll('[data-busy-origin]').forEach(el => delete el.dataset.busyOrigin);
    delete wrapper.dataset.cartTotalOptimistic;

    if (isBusy) {
        wrapper.dataset.cartBusy = 'true';
        if (originCard) originCard.dataset.busyOrigin = 'true';
        mmCartBusyTimeout = setTimeout(() => {
            mmSetCartBusy(false);
            if (typeof mmRefreshCartAndRecomputeGifts === 'function') mmRefreshCartAndRecomputeGifts();
        }, 8000);
    } else {
        delete wrapper.dataset.cartBusy;
    }
}

function mmCartIsBusy() {
    return getCartDrawerWrapper()?.dataset.cartBusy === 'true';
}

function mmGetCartLineCard(line) {
    return document.querySelector(`.mm-dc-product-container[data-line-index="${line}"]`)
        || document.querySelector(`[data-line-index="${line}"][data-line-key]`);
}

function mmChangeCartLine(line, card, quantity, productId) {
    if (card?.dataset.lineKey) return changeQuantity(card.dataset.lineKey, quantity, false, productId);
    return changeQuantity(line, quantity, true, productId);
}

window.mmRemoveCartLine = (line, productId) => {
    const card = mmGetCartLineCard(line);
    if (card) showLoaderOnPrice(card);
    mmChangeCartLine(line, card, 0, productId || card?.dataset.productId);
};

function mmStepCartLineQuantity(idx, step) {
	const it = mmGetCartLineCard(idx);
	if (!it) return;
    const qtyInput = it.querySelector('.mm-dc-product-qqty-number');
	const qty = parseInt(qtyInput?.dataset.itemQuantity || it.dataset.itemQuantity || qtyInput?.innerText || 1);
    const nextQty = Math.max(0, qty + step);
    if (qtyInput) {
        qtyInput.dataset.itemQuantity = nextQty;
        qtyInput.textContent = nextQty;
    }
	mmChangeCartLine(idx, it, nextQty, it.dataset.productId);
}

window.mmDecreaseProductQuantity = (idx) => mmStepCartLineQuantity(idx, -1);

window.mmIncreaseProductQuantity = (idx) => mmStepCartLineQuantity(idx, 1);

async function mmFoldRewards(predictItems) {
    const empty = { cart: null, gifts: [], giftUpdates: {} };
    try {
        const res = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER });
        if (!res.ok) return empty;
        const cart = await res.json();
        const actions = analyzeRewards({ ...cart, items: predictItems(cart.items || []) }, document);
        const gifts = (actions.itemsToAdd || []).filter(i => !mmRewardAddInFlight.has(String(i.id)));
        gifts.forEach(i => mmRewardAddInFlight.add(String(i.id)));
        if (gifts.length) setTimeout(() => gifts.forEach(i => mmRewardAddInFlight.delete(String(i.id))), 3000);
        return { cart, gifts, giftUpdates: actions.updates || {} };
    } catch (e) {
        return empty;
    }
}

function mmPostJSON(url, body) {
    return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
        body: JSON.stringify(body)
    });
}

function mmIsRoutineParent(item) {
    return (item?.properties?._cart_source || item?.properties?.cart_source) === 'routine_sur_mesure_parent';
}

function mmVisibleItemCount(cart) {
    if (!cart || cart.item_count === undefined) return undefined;
    if (!Array.isArray(cart.items)) return cart.item_count;
    return cart.items.reduce((count, item) => mmIsRoutineParent(item) ? count - item.quantity : count, cart.item_count);
}

function mmOrphanRoutineParentKeys(cart) {
    if (!cart || !Array.isArray(cart.items) || cart._is_scavenged) return [];
    const parentKeys = new Set(cart.items.map(item => item.parent_relationship?.parent_key).filter(Boolean));
    return cart.items.filter(item => mmIsRoutineParent(item) && !parentKeys.has(item.key)).map(item => item.key);
}

async function mmCartOpFailed(label, res) {
    const detail = res ? await res.text().catch(() => '') : '';
    console.error('[MM-Abo] ' + label + ':', res ? res.status : '', detail);
    await mmRefreshCartAndRecomputeGifts();
    mmSetCartBusy(false);
    return null;
}

function mmReadLineProperties(card) {
    try {
        let props = JSON.parse(card.dataset.lineProperties || '{}');
        if (Array.isArray(props)) {
            props = Object.fromEntries(props.filter(p => Array.isArray(p) && p.length === 2));
        }
        return props && typeof props === 'object' && Object.keys(props).length ? props : null;
    } catch (e) {
        return null;
    }
}

function mmPendingLine(variantId, qty, properties, sellingPlanId) {
    return {
        variant_id: parseInt(variantId, 10),
        key: null,
        quantity: qty,
        properties: properties || {},
        selling_plan_allocation: sellingPlanId ? { selling_plan: { id: parseInt(sellingPlanId, 10) } } : null
    };
}

function mmShowPendingRewards(plan) {
    const drawer = getCartDrawer();
    if (!drawer || !plan) return;

    Object.entries(plan.giftUpdates || {}).forEach(([key, qty]) => {
        if (qty !== 0) return;
        const line = Array.from(drawer.querySelectorAll('.mm-dc-free-product')).find(el => el.dataset.lineKey === key);
        if (!line || line.classList.contains('mm-dc-free-product-leaving')) return;
        line.classList.add('mm-dc-free-product-leaving');
        setTimeout(() => { if (line.isConnected) line.hidden = true; }, 220);
    });

    const content = drawer.querySelector('.mm-cart-content');
    if (!content) return;

    (plan.gifts || []).forEach(gift => {
        const rewardId = gift.properties && gift.properties._reward_id;
        const reward = Array.from(drawer.querySelectorAll('.mm-reward-data')).find(r => r.dataset.rewardId === rewardId);
        let product = null;
        try {
            product = JSON.parse((reward && reward.dataset.products) || '[]').find(p => String(p.variantId) === String(gift.id));
        } catch (e) {}
        if (!product) return;
        if (Array.from(content.querySelectorAll('.mm-dc-free-product-incoming')).some(c => c.dataset.cartItemId === String(gift.id))) return;

        const el = (tag, className, text) => {
            const node = document.createElement(tag);
            if (className) node.className = className;
            if (text != null) node.textContent = text;
            return node;
        };

        const card = el('div', 'mm-dc-product mm-dc-free-product mm-flex mm-gg-16 mm-dc-free-product-incoming');
        card.dataset.cartItemId = String(gift.id);

        const imageBox = el('div', 'mm-dc-product-image');
        if (product.image) {
            const img = el('img');
            img.src = product.image;
            img.width = 140;
            img.height = 140;
            img.alt = product.title || '';
            imageBox.appendChild(img);
        }

        const infos = el('div', 'mm-dc-product-infos mm-flex mm-column mm-align-start mm-gg-4');
        const titleBox = el('div', 'mm-flex mm-column mm-align-start');
        titleBox.appendChild(el('p', 'overtitre', product.title || ''));
        const tagBox = el('div', 'mm-flex mm-align-center mm-gg-8');
        tagBox.appendChild(el('p', 'mm-dc-free-tag overtitre', reward.dataset.freeLabel || ''));
        if (product.message) tagBox.appendChild(el('p', 'mm-dc-free-message text-xxs mm-black-400', product.message));
        infos.append(titleBox, tagBox);

        const side = el('div', 'mm-flex mm-column mm-justify-between mm-gg-8 mm-align-end');
        const price = el('div', 'mm-dc-product-price-wrapper mm-flex mm-align-center mm-gg-8');
        if (product.compareAtPrice > 0 && product.compareAtPriceFormatted) {
            const compare = el('p', 'mm-dc-product-compare-at-price text-xs mm-black-400');
            compare.appendChild(el('s', null, product.compareAtPriceFormatted));
            price.appendChild(compare);
        }
        price.appendChild(el('p', 'mm-dc-product-price text-xs-bold', reward.dataset.freeLabel || ''));
        side.appendChild(price);
        if (reward.dataset.removeLabel) {
            const remove = el('button', 'mm-dc-product-destroy mm-strip-btn');
            remove.type = 'button';
            remove.tabIndex = -1;
            remove.appendChild(el('span', 'text text-xs mm-black-400', reward.dataset.removeLabel));
            side.appendChild(remove);
        }

        const body = el('div', 'mm-dc-product-content mm-flex mm-justify-between mm-align-center mm-flex-grow mm-gg-16');
        body.append(infos, side);
        card.append(imageBox, body);

        const lines = Array.from(content.children).filter(c => c.matches('.mm-dc-product-container, .mm-dc-free-product'));
        const anchor = lines[lines.length - 1];
        if (anchor) anchor.after(card);
        else content.prepend(card);
    });
}

function mmPreviewSubscriptionGift(subscribing) {
    const drawer = getCartDrawer();
    if (!drawer) return;

    const activeSubscriptions = drawer.querySelectorAll('input[name="subscribe"]:checked, input[id^="mm-group-subscribe-"]:checked').length;
    const rewards = Array.from(drawer.querySelectorAll('.mm-reward-data[data-reward-type="free_product_abon"]'))
        .filter(r => r.dataset.offerActive === 'true');
    if (!rewards.length) return;

    if (!subscribing) {
        if (activeSubscriptions > 0) return;
        const rewardIds = new Set(rewards.map(r => r.dataset.rewardId));
        const giftUpdates = {};
        drawer.querySelectorAll('.mm-cart-content > .mm-dc-free-product[data-reward-id]').forEach(line => {
            if (rewardIds.has(line.dataset.rewardId) && line.dataset.lineKey) giftUpdates[line.dataset.lineKey] = 0;
        });
        mmShowPendingRewards({ gifts: [], giftUpdates });
        return;
    }

    if (activeSubscriptions !== 1) return;
    const gifts = [];
    rewards.forEach(r => {
        if (r.dataset.autoAddEnabled !== 'true') return;
        if (sessionStorage.getItem('mm_declined_reward_' + r.dataset.rewardId) === 'true') return;
        let products = [];
        try { products = JSON.parse(r.dataset.products || '[]'); } catch (e) {}
        if (products.length !== 1) return;
        const variantId = String(products[0].variantId);
        const alreadyThere = Array.from(drawer.querySelectorAll('.mm-cart-content > .mm-dc-free-product')).some(l => l.dataset.cartItemId === variantId);
        if (!alreadyThere) gifts.push({ id: variantId, properties: { _reward_id: r.dataset.rewardId } });
    });
    mmShowPendingRewards({ gifts, giftUpdates: {} });
}

function mmApplyNextState(card) {
    const next = card.querySelector('template.mm-dc-next-state')?.content;
    const priceBox = card.querySelector('.mm-dc-product-price-container');
    const nextPrice = next?.querySelector('[data-next-price]');
    if (!priceBox || !nextPrice) return false;

    priceBox.innerHTML = nextPrice.innerHTML;
    const wrapper = card.querySelector('.mm-dc-product-price-wrapper');
    wrapper?.querySelector('.mm-dc-product-price-reduce')?.remove();
    const nextBadge = next.querySelector('[data-next-badge]');
    if (wrapper && nextBadge) wrapper.insertAdjacentHTML('beforeend', nextBadge.innerHTML);

    const image = card.querySelector('.mm-dc-product-image');
    const nextImage = next.querySelector('[data-next-image] .mm-dc-product-image');
    if (image && nextImage) image.replaceWith(nextImage.cloneNode(true));

    const nextTotal = next.querySelector('[data-next-total]');
    const total = getCartDrawer()?.querySelector('.mm-cart-footer-cta [data-cart-total-price]');
    const drawerWrapper = getCartDrawerWrapper();
    if (nextTotal && total && drawerWrapper) {
        const freshTotal = total.cloneNode(false);
        freshTotal.textContent = nextTotal.textContent.trim();
        total.replaceWith(freshTotal);
        drawerWrapper.dataset.cartTotalOptimistic = 'true';
    }
    return true;
}

function mmSwapCartVariant(card, targetVariantId, sellingPlanId, line, qty) {
    return CartQueue.add(async () => {
        try {
            const oldKey = card.dataset.lineKey;
            const payload = { id: targetVariantId, quantity: qty };
            if (sellingPlanId) payload.selling_plan = sellingPlanId;
            const props = mmReadLineProperties(card);
            if (props) payload.properties = props;

            const plan = await mmFoldRewards(items => items
                .filter(i => i.key !== oldKey)
                .concat([mmPendingLine(targetVariantId, qty, props, sellingPlanId)]));
            mmShowPendingRewards(plan);

            if (!oldKey) {
                const remRes = await mmPostJSON(mmUrl('/cart/change.js'), { line: line.toString(), quantity: 0 });
                if (!remRes.ok) return mmCartOpFailed('Echec suppression de l ancienne ligne', remRes);
                const res = await mmPostJSON(mmUrl('/cart/add.js'), { items: [payload, ...plan.gifts], sections: getCartSectionId() });
                if (!res.ok) return mmCartOpFailed('Echec ajout du variant cible', res);
                await updateUI(await res.json(), card.dataset.productId);
                return res;
            }

            const addRes = await mmPostJSON(mmUrl('/cart/add.js'), { items: [payload, ...plan.gifts] });
            if (!addRes.ok) return mmCartOpFailed('Echec ajout du variant cible', addRes);

            const res = await mmPostJSON(mmUrl('/cart/update.js'), { updates: { ...plan.giftUpdates, [oldKey]: 0 }, sections: getCartSectionId() });
            if (!res.ok) return mmCartOpFailed('Echec suppression de l ancienne ligne', res);
            await updateUI(await res.json(), card.dataset.productId);
            return res;
        } catch (e) {
            console.error('[MM-Abo] Echec du swap de variant:', e);
            await mmRefreshCartAndRecomputeGifts();
            mmSetCartBusy(false);
            return null;
        }
    });
}

function mmToggleLinePlan(card, planId, qty) {
    return CartQueue.add(async () => {
        try {
            const key = card.dataset.lineKey;
            const plan = await mmFoldRewards(items => items.map(i => i.key === key
                ? { ...i, selling_plan_allocation: planId ? { selling_plan: { id: parseInt(planId, 10) } } : null }
                : i));
            mmShowPendingRewards(plan);

            const giftRemovals = Object.keys(plan.giftUpdates).length > 0;
            const giftAdds = plan.gifts.length > 0;
            const sections = { sections: getCartSectionId() };

            let res = await mmPostJSON(mmUrl('/cart/change.js'), { id: key, quantity: qty, selling_plan: planId || null, ...(giftRemovals || giftAdds ? {} : sections) });
            if (!res.ok) return mmCartOpFailed('Echec du changement de plan', res);

            if (giftRemovals) {
                res = await mmPostJSON(mmUrl('/cart/update.js'), { updates: plan.giftUpdates, ...(giftAdds ? {} : sections) });
                if (!res.ok) return mmCartOpFailed('Echec mise a jour des cadeaux', res);
            }
            if (giftAdds) {
                res = await mmPostJSON(mmUrl('/cart/add.js'), { items: plan.gifts, ...sections });
                if (!res.ok) return mmCartOpFailed('Echec ajout du cadeau', res);
            }

            await updateUI(await res.json(), card.dataset.productId);
            return res;
        } catch (e) {
            console.error('[MM-Abo] Echec du changement de plan:', e);
            await mmRefreshCartAndRecomputeGifts();
            mmSetCartBusy(false);
            return null;
        }
    });
}

window.toggleSellingPlan = (checkbox) => {
	const card = checkbox.closest(".mm-dc-product-container");
    if (!card) return;
    if (mmCartIsBusy()) { checkbox.checked = !checkbox.checked; return; }
    mmSetCartBusy(true, card);
    card.querySelector('.mm-dc-select-selling-plan-container')?.classList.toggle('hidden', !checkbox.checked);
    if (!mmApplyNextState(card)) showLoaderOnPrice(card);
    mmPreviewSubscriptionGift(checkbox.checked);

    const line = card.dataset.lineIndex;
    const qty = parseInt(card.querySelector(".mm-dc-product-qqty-number")?.innerText) || 1;

    // --- Logique Spéciale Routine (Bundling/Unbundling) ---
    const aboVariantsCsv = card.getAttribute('data-abo-unique-default-variant-ids') || '';
    const aboPlansCsv = card.getAttribute('data-abo-unique-default-plan-ids') || '';

    if (aboVariantsCsv && checkbox.checked) {
        // Mode Transformation Routine -> Items individuels (Abo Unique)
        const variantIds = aboVariantsCsv.split(',').map(v => parseInt(v, 10)).filter(Boolean);
        const planIds = aboPlansCsv.split(',').map(v => parseInt(v, 10) || null);
        const routineGroupId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

        const props = {
            _cart_source: 'abo_unique',
            _routine_group_id: routineGroupId,
            _routine_name: card.getAttribute('data-routine-name') || '',
            _routine_product_handle: card.getAttribute('data-routine-product-handle') || '',
            _routine_product_id: card.dataset.productId || '',
            _routine_product_image_url: card.getAttribute('data-routine-image-url') || '',
            _routine_variant_title: card.getAttribute('data-routine-variant-title') || ''
        };

        const items = variantIds.map((id, i) => {
            const item = { id, quantity: 1, properties: { ...props } };
            if (planIds[i]) item.selling_plan = planIds[i];
            return item;
        });

        CartQueue.add(async () => {
            try {
                const bundleKey = card.dataset.lineKey;
                const plan = await mmFoldRewards(cartItems => cartItems
                    .filter(i => i.key !== bundleKey)
                    .concat(items.map(it => mmPendingLine(it.id, it.quantity, it.properties, it.selling_plan))));
                mmShowPendingRewards(plan);

                let res;
                if (bundleKey) {
                    const addRes = await mmPostJSON(mmUrl('/cart/add.js'), { items: [...items, ...plan.gifts] });
                    if (!addRes.ok) return mmCartOpFailed('Echec de l eclatement de la routine', addRes);
                    res = await mmPostJSON(mmUrl('/cart/update.js'), { updates: { ...plan.giftUpdates, [bundleKey]: 0 }, sections: getCartSectionId() });
                } else {
                    const remRes = await mmPostJSON(mmUrl('/cart/change.js'), { line: line.toString(), quantity: 0 });
                    if (!remRes.ok) return mmCartOpFailed('Echec de l eclatement de la routine', remRes);
                    res = await mmPostJSON(mmUrl('/cart/add.js'), { items: [...items, ...plan.gifts], sections: getCartSectionId() });
                }
                if (!res.ok) return mmCartOpFailed('Echec de l eclatement de la routine', res);

                await updateUI(await res.json());
                // On force le refresh de tous les composants de la routine + de la routine elle même
                [parseInt(card.dataset.productId), ...variantIds].forEach(id => {
                    if (window.refreshProductCard) window.refreshProductCard(id);
                });
                return res;
            } catch (e) {
                console.error('[MM-Abo] Echec de l eclatement de la routine:', e);
                await mmRefreshCartAndRecomputeGifts();
                mmSetCartBusy(false);
                return null;
            }
        });
        return;
    }

    const variantAbo = card.dataset.variantAbo;
    const variantOnce = card.dataset.variantOnce;

    if (checkbox.checked && variantAbo) {
        const planId = card.querySelector('.mm-dc-cart-item-selling-plan-select')?.value || card.dataset.sellingPlanAbo;
        mmSwapCartVariant(card, variantAbo, planId, line, qty);
        return;
    }

    if (!checkbox.checked && variantOnce) {
        mmSwapCartVariant(card, variantOnce, null, line, qty);
        return;
    }

    const planId = checkbox.checked ? card.querySelector('.mm-dc-cart-item-selling-plan-select')?.value : null;
    if (card.dataset.lineKey) {
        mmToggleLinePlan(card, planId, qty);
    } else {
        changeQuantity(line, qty, true, card.dataset.productId, planId);
    }
};

function mmToggleCustomRoutineSubscription(checkbox) {
    const subscribe = checkbox.checked;
    if (mmCartIsBusy()) { checkbox.checked = !subscribe; return; }

    let lines = [];
    try { lines = JSON.parse(checkbox.dataset.routineLines || '[]'); } catch (e) { lines = []; }
    const changes = lines.filter(line => subscribe ? (!line.subscribed && line.sub) : (line.subscribed && line.once));
    if (!changes.length) { checkbox.checked = !subscribe; return; }

    const groupCard = checkbox.closest('.mm-dc-product-container');
    const groupId = checkbox.getAttribute('data-group-id');
    const parentKey = checkbox.dataset.routineParentKey || '';
    showLoaderOnPrice(groupCard);
    mmSetCartBusy(true, groupCard);
    mmPreviewSubscriptionGift(subscribe);

    const items = changes.map(line => {
        const properties = Array.isArray(line.properties)
            ? Object.fromEntries(line.properties.filter(p => Array.isArray(p) && p.length === 2))
            : (line.properties || {});
        const item = { id: subscribe ? line.sub : line.once, quantity: line.quantity, properties };
        if (subscribe && line.plan) item.selling_plan = line.plan;
        if (parentKey) item.parent_line_key = parentKey;
        return item;
    });
    const oldKeys = changes.map(line => line.key);
    const oldVariantIds = changes.map(line => String(line.key).split(':')[0]);

    CartQueue.add(async () => {
        try {
            const plan = await mmFoldRewards(cartItems => cartItems
                .filter(it => !oldKeys.includes(it.key))
                .concat(items.map(it => mmPendingLine(it.id, it.quantity, it.properties, it.selling_plan))));
            mmShowPendingRewards(plan);

            const addRes = await mmPostJSON(mmUrl('/cart/add.js'), { items: [...items, ...plan.gifts] });
            if (!addRes.ok) return mmCartOpFailed('Echec du changement d abonnement de la routine', addRes);

            const cart = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER }).then(r => r.ok ? r.json() : null).catch(() => null);
            const updates = { ...plan.giftUpdates };
            (cart?.items || []).forEach(it => {
                const inGroup = (it.properties?._routine_group_id || it.properties?.routine_group_id) === groupId;
                const wasSubscribed = !!it.selling_plan_allocation;
                if (inGroup && oldVariantIds.includes(String(it.variant_id)) && wasSubscribed !== subscribe) updates[it.key] = 0;
            });
            if (!cart) oldKeys.forEach(key => { updates[key] = 0; });

            const res = await mmPostJSON(mmUrl('/cart/update.js'), { updates, sections: getCartSectionId() });
            if (!res.ok) return mmCartOpFailed('Echec du changement d abonnement de la routine', res);

            await updateUI(await res.json());
            return res;
        } catch (e) {
            console.error('[MM-Abo] Echec du changement d abonnement de la routine:', e);
            await mmRefreshCartAndRecomputeGifts();
            mmSetCartBusy(false);
            return null;
        }
    });
}

window.toggleRoutineGroupSubscription = (checkbox) => {
    if (checkbox.dataset.routineCustom === 'true') {
        mmToggleCustomRoutineSubscription(checkbox);
        return;
    }
    // Si on decoche un groupe abo_unique, on fait l'inverse: on repasse en bundle single
    if (!checkbox.checked) {
        if (mmCartIsBusy()) { checkbox.checked = true; return; }
        const groupCard = checkbox.closest(".mm-dc-product-container");
        const groupId = checkbox.getAttribute('data-group-id');
        const routineVariantId = parseInt(checkbox.getAttribute('data-routine-variant-id'), 10);
        if (!groupId || !routineVariantId) { checkbox.checked = true; return; }
        showLoaderOnPrice(groupCard);
        mmSetCartBusy(true, groupCard);
        mmPreviewSubscriptionGift(false);

        CartQueue.add(async () => {
            try {
                const inGroup = it => (it.properties?._routine_group_id || it.properties?.routine_group_id) === groupId;
                const plan = await mmFoldRewards(cartItems => cartItems
                    .filter(it => !inGroup(it))
                    .concat([mmPendingLine(routineVariantId, 1, null, null)]));
                if (!plan.cart) return mmCartOpFailed('Panier illisible au degroupage de la routine', null);
                mmShowPendingRewards(plan);

                const updates = { ...plan.giftUpdates };
                plan.cart.items.forEach(it => { if (inGroup(it)) updates[it.key] = 0; });

                const addRes = await mmPostJSON(mmUrl('/cart/add.js'), { items: [{ id: routineVariantId, quantity: 1 }, ...plan.gifts] });
                if (!addRes.ok) return mmCartOpFailed('Echec du retour au bundle routine', addRes);

                const res = await mmPostJSON(mmUrl('/cart/update.js'), { updates, sections: getCartSectionId() });
                if (!res.ok) return mmCartOpFailed('Echec du degroupage de la routine', res);

                await updateUI(await res.json());
                // On rafraichit la routine + on force les cartes à se synchroniser
                window.refreshProductCard(parseInt(checkbox.getAttribute('data-routine-product-id')) || routineVariantId);
                syncCartState();
                return res;
            } catch (e) {
                console.error('[MM-Abo] Echec du decochage de la routine:', e);
                await mmRefreshCartAndRecomputeGifts();
                mmSetCartBusy(false);
                return null;
            }
        });
    }
};

window.updateSellingPlan = (line, element, planId) => {
    const card = element.closest(".mm-dc-product-container");
	const qty = parseInt(card?.querySelector(".mm-dc-product-qqty-number")?.innerText) || 1;
    if (card?.dataset.lineKey) changeQuantity(card.dataset.lineKey, qty, false, card.dataset.productId, planId);
	else changeQuantity(line, qty, true, card?.dataset.productId, planId);
};

window.mmAddFreeProductToCart = (btn) => {
    if (btn.classList.contains('is-loading')) return;
    const variantId = btn.dataset.variantId;
    const rewardId = btn.dataset.rewardId;
    if (!variantId) return;
    btn.classList.add('is-loading');
    btn.disabled = true;

    // Add loading class to wrapper if it exists (for CSS spinner)
    const wrapper = btn.closest('.mm-btn-add-wrapper');
    if (wrapper) wrapper.classList.add('mm-loading');

    // Lock all other reward buttons while this one is adding
    const rewardsDropdown = btn.closest('.mm-cart-rewards-dropdown');
    if (rewardsDropdown) rewardsDropdown.classList.add('mm-rewards-adding');

    CartQueue.add(async () => {
        try {
            // Clear declined status for this variant if it corresponds to a reward
            const allRewards = document.querySelectorAll('.mm-reward-data');
            allRewards.forEach(r => {
                try {
                    const prods = JSON.parse(r.getAttribute('data-products') || '[]');
                    if (prods.some(p => parseInt(p.variantId) == variantId)) {
                        const rId = r.getAttribute('data-reward-id');
                        sessionStorage.removeItem('mm_declined_reward_' + rId);
                    }
                } catch(e) {}
            });
            const rewardType = Array.from(allRewards).find(r => r.dataset.rewardId === rewardId)?.dataset.rewardType || '';

            const res = await fetch(mmUrl('/cart/add.js'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                body: JSON.stringify({
                    items: [{
                        id: parseInt(variantId),
                        quantity: 1,
                        properties: {
                            _is_reward: 'true',
                            _reward_id: rewardId || '',
                            _reward_type: rewardType,
                            _added_at: Date.now().toString()
                        }
                    }],
                    sections: getCartSectionId()
                })
            });
            if (res.ok) await updateUI(await res.json());
            else await mmRefreshCartAndRecomputeGifts();
            return res;
        } finally {
            btn.classList.remove('is-loading');
            btn.disabled = false;
            if (wrapper) wrapper.classList.remove('mm-loading');
            if (rewardsDropdown) rewardsDropdown.classList.remove('mm-rewards-adding');
        }
    });
};

window.mmRemoveFreeProduct = (btn, lineIndex, rewardId) => {
    // Si c'est un bouton dans une liste ou si l'ID est passé directement via onclick
    const finalRewardId = rewardId || btn.closest('.mm-dc-product-container')?.dataset.rewardId || btn.dataset.rewardId;
    if (finalRewardId) sessionStorage.setItem('mm_declined_reward_' + finalRewardId, 'true');
    const lineKey = btn.closest('[data-line-key]')?.dataset.lineKey;
    if (lineKey) changeQuantity(lineKey, 0, false);
    else changeQuantity(lineIndex, 0, true);
};

// --- FREE PRODUCT CODES DISPLAY ---
// Défini ici (toujours chargé) pour fonctionner même si le panier était vide au chargement initial
// et que mm-cart-footer-discount-applications.liquid n'a pas encore été rendu.

function mmSyncCodeRewardCards() {
    const codes = JSON.parse(sessionStorage.getItem('mm_applied_free_codes') || '[]')
        .map(c => c.toUpperCase().trim());
    document.querySelectorAll('.mm-cart-reward-item[data-required-code]').forEach(card => {
        const reqCode = (card.dataset.requiredCode || '').toUpperCase().trim();
        card.style.display = codes.includes(reqCode) ? '' : 'none';
    });
    document.querySelectorAll('.mm-cart-rewards-dropdown').forEach(wrapper => {
        const hasVisible = Array.from(wrapper.querySelectorAll('.mm-cart-reward-item'))
            .some(el => el.style.display !== 'none');
        wrapper.style.display = hasVisible ? '' : 'none';
    });
}

function mmDisplayFreeProductCodes() {
    // Always sync reward card visibility regardless of whether the codes list container exists.
    // The codes list (mm-free-product-codes-list) only exists when the cart is non-empty,
    // but mmSyncCodeRewardCards must run to show/hide free_product_code reward cards.
    mmSyncCodeRewardCards();

    const container = document.getElementById('mm-free-product-codes-list');
    if (!container) return;

    const freeCodes = JSON.parse(sessionStorage.getItem('mm_applied_free_codes') || '[]');
    container.innerHTML = '';

    if (freeCodes.length === 0) {
        container.style.display = 'none';
        return;
    }

    container.style.display = 'block';
    const freeText = document.querySelector('.mm-drawer-cart')?.dataset.freeText
                  || window.MM_CART_FREE_TEXT
                  || 'Gratuit';

    freeCodes.forEach(code => {
        const div = document.createElement('div');
        div.className = 'mm-flex mm-align-center mm-justify-between mm-gg-8 mm-mb-8';
        div.innerHTML = `
            <button type="button" onclick="removeFreeProductCode('${code}')" class="mm-discount-button mm-flex mm-align-center mm-gg-8 text-xs mm-bg-light-grey">
                ${code.toUpperCase()}
                <svg class="mm-icon-close" style="width: 12px; height: 12px;" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.5)" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
            <p class="mm-dc-discount-amount text-xs">${freeText}</p>
        `;
        container.appendChild(div);
    });
}

window.displayFreeProductCodes = mmDisplayFreeProductCodes;
window.syncCodeRewardCards = mmSyncCodeRewardCards;

window.removeFreeProductCode = async function(code) {
    const freeCodes = JSON.parse(sessionStorage.getItem('mm_applied_free_codes') || '[]');
    sessionStorage.setItem('mm_applied_free_codes', JSON.stringify(
        freeCodes.filter(c => c.toLowerCase() !== code.toLowerCase())
    ));

    const codeUpper = code.toUpperCase().trim();
    const rewardEl = Array.from(document.querySelectorAll('.mm-reward-data[data-reward-type="free_product_code"]'))
        .find(el => (el.dataset.requiredCode || '').toUpperCase().trim() === codeUpper);

    let rewardVariantIds = [];
    if (rewardEl) {
        try { rewardVariantIds = JSON.parse(rewardEl.dataset.products || '[]').map(p => String(p.variantId)); } catch (e) {}
    }
    const rewardId = rewardEl?.dataset.rewardId;

    await CartQueue.add(async () => {
        const cart = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER })
            .then(r => r.ok ? r.json() : null)
            .catch(() => null);
        if (!cart) return null;
        const updates = {};
        (cart.items || []).forEach(item => {
            const isThisReward = (rewardId && item.properties?._reward_id === rewardId)
                || (rewardVariantIds.includes(String(item.variant_id)) && (item.properties?._is_reward === 'true' || item.final_line_price === 0));
            if (isThisReward) updates[item.key] = 0;
        });
        const remainingCodes = (cart.discount_codes || [])
            .map(d => d.code)
            .filter(c => c && c.toUpperCase().trim() !== codeUpper);
        const res = await fetch(mmUrl('/cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
            body: JSON.stringify({ updates, discount: remainingCodes.join(','), sections: getCartSectionId() })
        });
        if (res.ok) await updateUI(await res.json());
        return res;
    });
    mmDisplayFreeProductCodes();
};

function showDiscountError(message) {
    const el = document.getElementById('discount-error');
    if (!el) return;
    el.innerHTML = `<span>${message}</span><button id="discount-error-dismiss" type="button" aria-label="${el.dataset.dismissLabel || 'Close'}" onclick="this.closest('#discount-error').style.display='none'">✕</button>`;
    el.style.display = 'flex';
}

function clearDiscountError() {
    const el = document.getElementById('discount-error');
    if (el) el.style.display = 'none';
}

window.mmApplyDiscountInCart = async (evt) => {
    if (evt) { evt.preventDefault(); evt.stopPropagation(); }
    const codeInput = document.getElementById('discount-code');
    const code = codeInput?.value.trim().toUpperCase();
    if (!code) return;

    const applyBtn = document.getElementById('apply-discount');
    const setLoading = (loading) => {
        if (!applyBtn) return;
        applyBtn.classList.toggle('is-loading', loading);
        applyBtn.disabled = loading;
    };

    setLoading(true);

    // Tout dans un seul CartQueue.add : le lookup rewardData se fait APRÈS que toute opération
    // précédente (add-to-cart, etc.) ait terminé et mis à jour le DOM via applySection.
    CartQueue.add(async () => {
        // Lookup ici, DOM à jour garanti (queue FIFO)
        const rewardData = document.querySelector(`.mm-reward-data[data-reward-type="free_product_code"][data-required-code="${CSS.escape(code)}"]`);
        const conditionsErrorText = rewardData?.dataset.errorText || codeInput?.dataset.conditionsErrorText || codeInput?.dataset.errorText || 'Invalid code';

        try {
            clearDiscountError();

            const previousCart = rewardData ? null : await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER })
                .then(r => r.ok ? r.json() : null)
                .catch(() => null);
            const previousCodes = (previousCart?.discount_codes || [])
                .filter(d => d.applicable && (d.code || '').toUpperCase() !== code)
                .map(d => d.code);

            const attemptUpdate = () => fetch(mmUrl('/cart/update.js'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                body: JSON.stringify({ discount: code, sections: getCartSectionId() })
            });

            let res = await attemptUpdate();
            // Retry once on 409 Conflict (concurrent cart modification)
            if (res.status === 409) {
                await new Promise(r => setTimeout(r, 600));
                res = await attemptUpdate();
            }

            if (rewardData) {
                // --- Logique "Code Spécial" (Rewards) ---
                // Shopify BXGY ne peuple discount_applications qu'après ajout du produit → on valide via threshold JS.
                if (!res.ok) { return res; }

                const data = await res.json();

                const freshRewardData = document.querySelector(
                    `.mm-reward-data[data-reward-type="free_product_code"][data-required-code="${CSS.escape(code)}"]`
                );
                const threshold = parseInt(freshRewardData?.dataset.threshold || rewardData.dataset.threshold || '0');
                const cartTotal = data.total_price || 0;
                const offerActive = (freshRewardData || rewardData).dataset.offerActive === 'true';
                const discountAccepted = offerActive && (threshold === 0 || cartTotal >= threshold);

                if (discountAccepted) {
                    const freeCodes = JSON.parse(sessionStorage.getItem('mm_applied_free_codes') || '[]');
                    if (!freeCodes.includes(code)) {
                        freeCodes.push(code);
                        sessionStorage.setItem('mm_applied_free_codes', JSON.stringify(freeCodes));
                    }
                }

                await updateUI(data);
                if (codeInput) codeInput.value = '';
                if (!discountAccepted) { showDiscountError(conditionsErrorText); }

            } else {
                // Comportement standard Shopify (codes sans bloc reward associé)
                if (res.ok) {
                    let data = await res.json();
                    const enteredCode = (data.discount_codes || []).find(d => (d.code || '').toUpperCase() === code);
                    if (enteredCode && enteredCode.applicable === false) {
                        const restoreRes = await fetch(mmUrl('/cart/update.js'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                            body: JSON.stringify({ discount: previousCodes.join(','), sections: getCartSectionId() })
                        }).catch(() => null);
                        if (restoreRes && restoreRes.ok) data = await restoreRes.json();
                        await updateUI(data);
                        const freshCodeInput = document.getElementById('discount-code');
                        if (freshCodeInput) freshCodeInput.value = code;
                        showDiscountError(codeInput?.dataset.errorText || 'Invalid code');
                        return res;
                    }
                    await updateUI(data);
                    if (codeInput) codeInput.value = '';
                } else {
                    const errData = await res.json().catch(() => ({}));
                    showDiscountError(errData?.description || codeInput?.dataset.errorText || 'Invalid code');
                }
                return res;
            }

        } catch(e) {
            window.log('error', '🛒 [CART]', 'Erreur code reward', e.message);
            showDiscountError(conditionsErrorText);
        } finally {
            setLoading(false);
            mmDisplayFreeProductCodes();
        }
    });
};

window.removeDiscount = (code) => {
    CartQueue.add(async () => {
        const target = String(code || '').toUpperCase().trim();
        const cart = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER })
            .then(r => r.ok ? r.json() : null)
            .catch(() => null);
        const remainingCodes = (cart?.discount_codes || [])
            .map(d => d.code)
            .filter(c => c && c.toUpperCase().trim() !== target);
        const res = await fetch(mmUrl('/cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
            body: JSON.stringify({ discount: remainingCodes.join(','), sections: getCartSectionId() })
        });
        if (res.ok) await updateUI(await res.json());
        return res;
    });
};

async function refreshProductCard(productId) {
	if (!productId) return;
    return (async () => {
        try {
            const cards = document.querySelectorAll(
                `.mm-product-card-2[data-product-id="${productId}"], .mm-product-card-2[data-product-id-linked="${productId}"]`
            );
            if (!cards.length) return null;
            const cardsByView = new Map();
            cards.forEach(card => {
                const view = card.dataset.cardView || 'card-product-no-delete';
                if (!cardsByView.has(view)) cardsByView.set(view, []);
                cardsByView.get(view).push(card);
            });
            let response = null;
            let refreshed = false;
            for (const [view, viewCards] of cardsByView) {
                const handle = viewCards[0].dataset.productHandle;
                response = await fetch(mmUrl(`/products/${encodeURIComponent(handle)}?view=${view}`), { headers: { ...MM_AUTH_HEADER } });
                if (!response.ok) continue;
                const temp = document.createElement('div');
                temp.innerHTML = await response.text();
                const newCardContent = temp.querySelector('.mm-product-card-2');
                if (newCardContent) {
                    viewCards.forEach(card => {
                        card.innerHTML = newCardContent.innerHTML;
                    });
                }
                refreshed = true;
            }
            if (refreshed) mmPostUpdateUI(false);
            return response;
        } catch(e) { return null; }
    })();
}
window.refreshProductCard = refreshProductCard;

async function mmApplyFormDiscount(formData, data) {
    const code = (formData.get('mm_discount_code') || '').trim();
    if (!code) return data;
    try {
        const cartRes = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER });
        const cart = cartRes.ok ? await cartRes.json() : null;
        const codes = (cart?.discount_codes || [])
            .map(discount => discount.code)
            .filter(existing => existing && existing.toUpperCase() !== code.toUpperCase());
        codes.push(code);
        const res = await fetch(mmUrl('/cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
            body: JSON.stringify({ discount: codes.join(','), sections: getCartSectionId() })
        });
        return res.ok ? await res.json() : data;
    } catch (e) {
        return data;
    }
}

function mmShowAddToCartError(form, message) {
    if (!form) return;
    const text = message || window.cartStrings?.error;
    if (!text) return;
    let errorEl = form.nextElementSibling?.classList.contains('mm-cart-add-error') ? form.nextElementSibling : null;
    if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'mm-cart-add-error text-xs';
        errorEl.setAttribute('role', 'alert');
        form.insertAdjacentElement('afterend', errorEl);
    }
    errorEl.textContent = text;
}

document.addEventListener('submit', (e) => {
    // On ne traite que les formulaires d'ajout au panier direct
    const form = e.target.closest('form[action$="/cart/add"]');
    if (!form) return;
    if (form.nextElementSibling?.classList.contains('mm-cart-add-error')) form.nextElementSibling.remove();

    // Sécurité: si c'est le formulaire principal du panier (checkout), on l'ignore ici
    // Cela évite que l'appui sur "Entrée" dans le champ promo ne déclenche l'ajax d'ajout
    if (form.getAttribute('action') === '/cart' || form.id === 'cart') {
        return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
		const formData = new FormData(form);
        // Use submitter if available (modern browsers), otherwise fallback to form search
        const submitBtn = e.submitter || form.querySelector('[type="submit"]');
        
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.classList.add('is-loading');
        }

        CartQueue.add(async () => {
          //   console.log("[MM-Queue] Ajout au panier en cours...");
            try {
                const body = { 
                    id: formData.get('id'), 
                    quantity: formData.get('quantity') || 1, 
                    sections: getCartSectionId() 
                };
                
                // Gestion ReCharge / Subscriptions
                const purchaseOption = formData.get('purchase_option');
                const sellingPlan = formData.get('selling_plan');

                // --- Logique Spéciale Routine (Bundling/Unbundling au premier ajout) ---
                const routineName = formData.get('mm_routine_name');
                if (routineName && (purchaseOption === 'abonnement' || (!purchaseOption && sellingPlan && sellingPlan !== '0'))) {
                    let subSellingPlans = form.querySelectorAll('.mm-dc-sub-selling-plan');
                    if (subSellingPlans.length === 0 && form.dataset.mmReloadKey === 'buy-form-sticky') {
                        const mainForm = form.closest('[data-section-main-product]')?.querySelector('[data-cta] form[data-mm-reload-key="buy-form-main"]');
                        if (mainForm) subSellingPlans = mainForm.querySelectorAll('.mm-dc-sub-selling-plan');
                    }
                    if (subSellingPlans.length > 0) {
                        const routineGroupId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
                        const addedAt = Date.now().toString();
                        const props = {
                            _cart_source: 'abo_unique',
                            _routine_group_id: routineGroupId,
                            _routine_name: routineName,
                            _routine_product_handle: formData.get('mm_routine_product_handle') || '',
                            _routine_product_id: formData.get('mm_routine_product_id') || '',
                            _routine_product_image_url: formData.get('mm_routine_product_image_url') || '',
                            _routine_variant_title: formData.get('mm_routine_variant_title') || '',
                            _added_at: addedAt
                        };

                        const items = Array.from(subSellingPlans).map(select => {
                            const vId = select.dataset.subVariantId;
                            const sPlan = select.value;
                            const item = { id: vId, quantity: 1, properties: { ...props } };
                            if (sPlan) item.selling_plan = sPlan;
                            return item;
                        });

                        const res = await fetch(mmUrl('/cart/add.js'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                            body: JSON.stringify({ items, sections: getCartSectionId() })
                        });

                        if (!res.ok && res.status !== 429) {
                            const errData = await res.json().catch(() => ({}));
                            mmShowAddToCartError(form, errData.description || errData.message);
                        }
                        if (res.ok) {
                            const data = await mmApplyFormDiscount(formData, await res.json());
                            if (formData.get('return_to')) {
                                await updateUI(data);
                                window.location.href = formData.get('return_to');
                            } else {
                                await updateUI(data);
                                openCart();
                            }
                        }
                        if (submitBtn) {
                            submitBtn.disabled = false;
                            submitBtn.classList.remove('is-loading');
                        }
                        return res;
                    }
                }
                
                body.properties = {};
                for (const [key, value] of formData.entries()) {
                    const propertyMatch = key.match(/^properties\[(.+)\]$/);
                    if (propertyMatch && value !== '') {
                        body.properties[propertyMatch[1]] = value;
                    }
                }

                if (purchaseOption === 'abonnement' || (!purchaseOption && sellingPlan && sellingPlan !== '0')) {
                    if (sellingPlan && sellingPlan !== '0') {
                        body.selling_plan = sellingPlan;
                    }
                } else if (purchaseOption === 'once') {
                    delete body.selling_plan;
                }

                const res = await fetch(mmUrl('/cart/add.js'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                    body: JSON.stringify(body)
                });
                if (!res.ok && res.status !== 429) {
                    const errData = await res.json().catch(() => ({}));
                    console.error("[MM-Queue] Echec ajout (" + res.status + "):", errData);
                    mmShowAddToCartError(form, errData.description || errData.message);
                }
                if (res.ok) {
                    const added = await res.json();
                    const data = await mmApplyFormDiscount(formData, added);
                    // console.log("[MM-Queue] Ajout réussi.");
                    await updateUI(data, added.product_id || body.id);
                    if (formData.get('return_to')) {
                        window.location.href = formData.get('return_to');
                    } else {
                        openCart();
                    }
                }
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.classList.remove('is-loading');
                }
                return res;
            } catch(e) { 
                console.error("[MM-Queue] Echec ajout au panier:", e);
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.classList.remove('is-loading');
                }
                return null; 
            }
        });
}, true);

window.mmAddItemsToCart = (items) => CartQueue.add(async () => {
    try {
        const res = await mmPostJSON(mmUrl('/cart/add.js'), { items, sections: getCartSectionId() });
        if (res.status === 429) return res;
        if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            console.error('[MM-Queue] Echec ajout groupe (' + res.status + '):', errData);
            return { ok: false, status: res.status, message: errData.description || errData.message };
        }
        await updateUI(await res.json());
        openCart();
        return { ok: true };
    } catch (e) {
        console.error('[MM-Queue] Echec ajout groupe:', e);
        return { ok: false };
    }
});

window.mmOpenCart = function() {
    openCart();
    mmPostUpdateUI(false);
};
window.mmCloseCart = () => { 
    getCartDrawerWrapper()?.classList.remove('mm-dc-is-open'); 
    document.body.classList.remove('mm-cart-open');
    document.documentElement.style.overflow = '';
    if (typeof deactivateFocusTrap === 'function') deactivateFocusTrap();
};

window.mmRefreshCartAndRecomputeGifts = async () => {
    CartQueue.add(async () => {
        try {
            const res = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER });
            if (res.ok) {
                const data = await res.json();
                await mmSynchronizeRewards(data, true); // true = immediate to avoid deadlock
                await updateUI(data);
            }
        } catch(e) { console.error("Error refreshing gifts:", e); }
    });
};

let mmImageModalTrigger = null;

window.mmOpenImageModal = (src, alt) => {
    const modal = document.getElementById('mmCartImageModal');
    const img = document.getElementById('mmCartImageModalImg');
    if (modal && img) {
        img.src = src;
        if (alt) img.alt = alt;
        mmImageModalTrigger = document.activeElement;
        modal.classList.add('mm-open');
        modal.setAttribute('aria-hidden', 'false');
        modal.querySelector('.mm-image-modal__close')?.focus();
    }
};

window.mmCloseImageModal = () => {
    const modal = document.getElementById('mmCartImageModal');
    if (!modal) return;
    modal.classList.remove('mm-open');
    modal.setAttribute('aria-hidden', 'true');
    if (mmImageModalTrigger && document.contains(mmImageModalTrigger)) mmImageModalTrigger.focus();
    mmImageModalTrigger = null;
};

let noteTimeout;
window.mmSaveCartNote = (textarea, immediate = false) => {
    clearTimeout(noteTimeout);
    const doSave = async () => {
        const note = textarea.value;
        await fetch(mmUrl('/cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
            body: JSON.stringify({ note })
        });
    };

    if (immediate) {
        doSave();
    } else {
        noteTimeout = setTimeout(doSave, 500);
    }
};

function mmPostUpdateUI(releaseBusy = true) {
    if (releaseBusy) mmSetCartBusy(false);
    if (window.handleDropdown) window.handleDropdown();
	if (window.initAccordions) window.initAccordions();
    mmInitSplideCross();
    mmInitSplideRewards();

    if (typeof initializeSubscription === 'function') {
        initializeSubscription();
    }
    
    // Attach Note Listener
    const noteArea = document.getElementById('CartNote');
    if (noteArea && !noteArea.dataset.listening) {
        noteArea.addEventListener('input', (e) => mmSaveCartNote(e.target, false));
        noteArea.addEventListener('change', (e) => mmSaveCartNote(e.target, true));
        noteArea.dataset.listening = true;
    }

    // Attach Image Zoom Listeners
    document.querySelectorAll('.mm-image-zoom-trigger').forEach(img => {
        if (!img.dataset.zoomAttached) {
            img.style.cursor = 'zoom-in';
            img.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const innerImg = img.querySelector('img');
                const alt = innerImg?.alt || img.alt || '';
                mmOpenImageModal(img.dataset.fullUrl || innerImg?.src || img.src, alt);
            });
            img.dataset.zoomAttached = true;
        }
    });
}

function updateCartCount(data) {
    const doUpdate = (d) => {
        const count = mmVisibleItemCount(d);
        document.querySelectorAll('.mm-cart-count-bubble').forEach(b => {
            b.textContent = count;
            b.style.display = parseInt(count) > 0 ? 'flex' : 'none';
        });
    };

    if (data && data.item_count !== undefined) {
        doUpdate(data);
    } else {
        fetch(mmUrl('/cart.js')).then(r => r.json()).then(doUpdate).catch(e => {});
    }
}

// --- DELEGATED EVENTS ---
document.addEventListener('click', (e) => {
    // 1. Checkbox cadeau footer
    const giftCb = e.target.closest('.mm-dc-gift-checkbox');
    if (giftCb) {
        const variantId = giftCb.dataset.variantId;
        const rewardId = giftCb.dataset.rewardId;
        if (giftCb.checked) {
            if (rewardId) sessionStorage.removeItem('mm_declined_reward_' + rewardId);
            CartQueue.add(async () => {
                const res = await fetch(mmUrl('/cart/add.js'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                    body: JSON.stringify({ id: variantId, quantity: 1, sections: getCartSectionId() })
                });
                if (res.ok) await updateUI(await res.json());
                return res;
            });
        } else {
            if (rewardId) sessionStorage.setItem('mm_declined_reward_' + rewardId, 'true');
            CartQueue.add(async () => {
                const cart = await fetch(mmUrl('/cart.js'), { headers: MM_AUTH_HEADER })
                    .then(r => r.ok ? r.json() : null)
                    .catch(() => null);
                const item = cart?.items?.find(it => it.variant_id.toString() === variantId.toString());
                if (!item) return null;
                const res = await fetch(mmUrl('/cart/change.js'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                    body: JSON.stringify({ id: item.key, quantity: 0, sections: getCartSectionId() })
                });
                if (res.ok) await updateUI(await res.json());
                return res;
            });
        }
        return;
    }

    // 2. Add free product
    const freeBtn = e.target.closest('.mm-btn-add-free-product');
    if (freeBtn) {
        window.mmAddFreeProductToCart(freeBtn);
        return;
    }

    // 3. Remove routine group
    const remGroup = e.target.closest('.mm-remove-routine-group');
    if (remGroup) {
        // Visual Feedback
        showLoaderOnPrice(remGroup.closest('.mm-dc-product-container'));
        
        const groupId = remGroup.dataset.removeRoutineGroup;
        CartQueue.add(async () => {
            try {
                // Fetch full cart to identify items in the group
                const cart = await fetch(mmUrl('/cart.js')).then(r => r.json());
                const updates = {};
                const inGroup = it => (it.properties?._routine_group_id || it.properties?.routine_group_id) === groupId;
                const isCustomLine = it => (it.properties?._cart_source || it.properties?.cart_source) === 'routine_sur_mesure';
                const splitOnce = cart.items.some(it => inGroup(it) && isCustomLine(it) && it.selling_plan_allocation);
                const keptLines = splitOnce ? cart.items.filter(it => inGroup(it) && isCustomLine(it) && !it.selling_plan_allocation) : [];

                cart.items.forEach((it, i) => {
                    if (inGroup(it)) {
                        // Use key if available, otherwise 1-based line index
                        updates[it.key || (i + 1)] = 0;
                    }
                });

                if (keptLines.length > 0) {
                    const addRes = await mmPostJSON(mmUrl('/cart/add.js'), { items: keptLines.map(it => ({ id: it.variant_id, quantity: it.quantity })) });
                    if (!addRes.ok) keptLines.forEach(it => { delete updates[it.key]; });
                }

                if (Object.keys(updates).length > 0) {
                     // Batch update: Remove all items in one request
                     const res = await fetch(mmUrl('/cart/update.js'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...MM_AUTH_HEADER },
                        body: JSON.stringify({ updates, sections: getCartSectionId() })
                    });
                    if (res.ok) await updateUI(await res.json());
                    return res;
                }
            } catch(e) { console.error("Error removing group", e); }
        });
        return;
    }
    // 4. Accordion Toggle (Delegated fallback for AJAX content)
    const accordionToggle = e.target.closest('[data-accordion-toggle]');
    if (accordionToggle && accordionToggle.closest('.mm-drawer-cart')) {
        // If mm-global already bound this, respect it and do nothing (let the bound listener handle it)
        if (accordionToggle.dataset.accordionBound === 'true') return;

        const targetId = accordionToggle.getAttribute("data-accordion-toggle");
        const content = document.querySelector(`[data-accordion-content="${targetId}"]`);
        if (content) {
            e.preventDefault();
            accordionToggle.classList.toggle("mm-open");
            accordionToggle.setAttribute("aria-expanded", String(accordionToggle.classList.contains("mm-open")));
            content.style.maxHeight = content.style.maxHeight ? null : content.scrollHeight + "px";
        }
        return;
    }
});

document.addEventListener('change', (e) => {
    // Handling frequency change for sub-items in a routine
    const subSellingPlan = e.target.closest('.mm-dc-sub-selling-plan');
    if (subSellingPlan && subSellingPlan.dataset.lineIndex) {
        const line = subSellingPlan.dataset.lineIndex;
        const planId = subSellingPlan.value;
        const productId = subSellingPlan.dataset.subProductId;
        const quantity = parseInt(subSellingPlan.dataset.itemQuantity) || 1;
        
     //    console.log(`[MM-Cart] Updating sub-item frequency: Line ${line}, Plan ${planId}, Qty ${quantity}`);
        
        // Show loader on the closest product container
        showLoaderOnPrice(subSellingPlan.closest('.mm-dc-product-container') || subSellingPlan.closest('.mm-dc-sub-product'));

        if (subSellingPlan.dataset.lineKey) changeQuantity(subSellingPlan.dataset.lineKey, quantity, false, productId, planId);
        else changeQuantity(line, quantity, true, productId, planId);
    }
});

document.addEventListener('DOMContentLoaded', () => {
    mmInitSplideCross();
    mmInitSplideRewards();
    updateCartCount();
    // Snapshot initial & synchro des cadeaux
    fetch(mmUrl('/cart.js')).then(r => r.json()).then(data => {
        if (data && data.items) {
            cartSnapshot = Array.from(new Set(data.items.map(it => it.product_id)));
            // On vérifie tout de suite si des cadeaux doivent être ajoutés/supprimés
            mmSynchronizeRewards(data);
        }
    }).catch(() => {});
});
